/**
 * Stale-client detection
 * ──────────────────────
 * Some installed copies of the PWA keep running an old build (pinned to an old
 * deployment URL, or an aggressively cached app shell). The result is athletes
 * silently missing fixes — and old removed behavior (e.g. the Galesburg
 * weather fallback) living on.
 *
 * The build id is baked into the bundle at build time (vite define) and also
 * emitted as /version.json on the canonical domain. On app open (and whenever
 * the app returns to the foreground) we compare the two:
 *  - mismatch → reload once (fixes plain HTTP-cache staleness);
 *  - still mismatched after that reload → this origin can never update
 *    (old deployment URL) → show a banner telling the athlete to reinstall
 *    from the canonical domain.
 *
 * Checked against the CANONICAL domain, not location.origin, precisely so a
 * copy pinned to an old deployment URL still learns it is outdated.
 */

declare const __BUILD_ID__: string;

const CANONICAL = 'https://www.kaderrunning.ai';
const RELOADED_KEY = 'kader-update-reloaded-for';
const CHECK_INTERVAL = 15 * 60 * 1000;

let bannerShown = false;

function showReinstallBanner() {
  if (bannerShown || document.getElementById('kader-stale-banner')) return;
  bannerShown = true;
  const el = document.createElement('div');
  el.id = 'kader-stale-banner';
  el.style.cssText = [
    'position:fixed', 'top:0', 'left:0', 'right:0', 'z-index:9999',
    'padding:10px 16px', 'background:#1C1708', 'border-bottom:1px solid #E8731A55',
    'color:#F2F2F2', 'font-size:12px', 'line-height:1.5', 'text-align:center',
    'font-family:system-ui,sans-serif',
  ].join(';');
  el.innerHTML = 'This copy of Kader is outdated and can’t update itself. '
    + '<a href="https://www.kaderrunning.ai" style="color:#E8731A;font-weight:600">Open kaderrunning.ai</a>'
    + ' and re-add it to your home screen.';
  document.body.appendChild(el);
}

async function checkForUpdate() {
  try {
    const res = await fetch(`${CANONICAL}/version.json?_=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return;
    const { buildId } = await res.json();
    if (!buildId || typeof buildId !== 'string') return;

    if (buildId === __BUILD_ID__) {
      try { sessionStorage.removeItem(RELOADED_KEY); } catch { /* ignore */ }
      return;
    }

    // Stale. Reload once per deployed build id; if we already reloaded for
    // this id and are still stale, reloading will never help on this origin.
    let alreadyReloaded = false;
    try {
      alreadyReloaded = sessionStorage.getItem(RELOADED_KEY) === buildId;
      if (!alreadyReloaded) sessionStorage.setItem(RELOADED_KEY, buildId);
    } catch { /* storage unavailable → banner rather than a reload loop */ alreadyReloaded = true; }

    if (alreadyReloaded) {
      console.warn(`Kader client is stale (running ${__BUILD_ID__}, deployed ${buildId}) and reload did not update it.`);
      showReinstallBanner();
    } else {
      window.location.reload();
    }
  } catch { /* offline or blocked — try again next cycle */ }
}

if (import.meta.env.PROD) {
  // First check shortly after boot (let the app settle), then on every return
  // to the foreground — the natural moment a phone reopens a day-old PWA.
  setTimeout(checkForUpdate, 3000);
  setInterval(checkForUpdate, CHECK_INTERVAL);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkForUpdate();
  });
}
